import React, { useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  ShoppingBagIcon, 
  UsersIcon, 
  ChartBarIcon,
  UserGroupIcon,
  Cog6ToothIcon as CogIcon,
  ArrowRightOnRectangleIcon as LogoutIcon,
  HomeIcon,
  FolderIcon,
  PhotoIcon,
  ArrowPathIcon
} from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';

const navItems = [
  { name: 'Dashboard', path: '/admin/dashboard', icon: ChartBarIcon },
  { name: 'PitWall', path: '/admin/pitwall', icon: ArrowPathIcon },
  { name: 'Racers', path: '/admin/racers', icon: UserGroupIcon },
  { name: 'Design Projects', path: '/admin/projects', icon: FolderIcon },
  { name: 'Portfolio', path: '/admin/portfolio', icon: PhotoIcon },
  { name: 'Users', path: '/admin/users', icon: UsersIcon },
  { name: 'Settings', path: '/admin/settings', icon: CogIcon },
];

export const AdminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, isAdmin, isAuthenticated, isLoading } = useAuth();


  useEffect(() => {
    if (!isLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/login', { state: { from: location } });
    }
  }, [isLoading, isAuthenticated, isAdmin, navigate, location]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-black via-purple-900 to-teal-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400 shadow-lg shadow-teal-500/50"></div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-black via-purple-900 to-teal-900">
      {/* Sidebar */}
      <div className="w-64 bg-black/40 backdrop-blur-xl border-r border-teal-500/20">
        <div className="p-4 border-b border-teal-500/20 bg-black/20">
          <Link to="/" className="block">
            <h1 className="text-xl font-bold bg-gradient-to-r from-teal-400 to-purple-400 text-transparent bg-clip-text hover:from-teal-300 hover:to-purple-300 transition-all duration-200">
              RaceCarly Admin
            </h1>
          </Link>
        </div>
        <nav className="mt-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === '/admin/dashboard'
              ? location.pathname === '/admin' || location.pathname.startsWith('/admin/dashboard')
              : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-4 py-3 text-gray-300 hover:bg-teal-900/30 hover:text-teal-300 transition-all duration-200 group ${
                  isActive ? 'bg-gradient-to-r from-teal-900/40 to-purple-900/40 text-teal-300 border-r-4 border-teal-400' : ''
                }`}
              >
                <Icon className={`w-5 h-5 mr-3 transition-transform duration-200 group-hover:scale-110 ${
                  isActive ? 'text-teal-400' : 'text-gray-400 group-hover:text-teal-400'
                }`} />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-3 text-gray-300 hover:bg-red-900/30 hover:text-red-400 transition-all duration-200 group mt-4"
          >
            <LogoutIcon className="w-5 h-5 mr-3 text-gray-400 group-hover:text-red-400 transition-transform duration-200 group-hover:scale-110" />
            <span className="font-medium">Logout</span>
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto bg-black/10 backdrop-blur-sm">
        {/* Header */}
        <header className="bg-black/40 backdrop-blur-xl border-b border-teal-500/20">
          <div className="px-6 py-4">
            <h2 className="text-xl font-semibold bg-gradient-to-r from-teal-400 to-purple-400 text-transparent bg-clip-text">
              {navItems.find(item => location.pathname.startsWith(item.path))?.name || 'Dashboard'}
            </h2>
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          <div className="bg-black/20 backdrop-blur-md rounded-lg border border-teal-500/10 shadow-xl shadow-teal-900/20">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}; 